/**
 * Reset documents utility
 * This will clear the existing documents and reinitialize with only 5 documents
 */

import { initializeSampleData } from './sampleData';

// Generate dates within a range
const randomDate = (start, end) => {
  const startDate = start || new Date(new Date().setDate(new Date().getDate() - 30));
  const endDate = end || new Date();
  return new Date(startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime())).toISOString().split('T')[0];
};

// Document statuses with weighted probability
const documentStatuses = ['paid', 'paid', 'paid', 'pending', 'pending', 'overdue'];

// Reset documents to only 5
export const resetDocuments = () => {
  // Get existing clients and products
  const clientsData = localStorage.getItem('facit_clients');
  const productsData = localStorage.getItem('facit_products');
  
  const clients = clientsData ? JSON.parse(clientsData) : [];
  const products = productsData ? JSON.parse(productsData) : [];
  
  if (clients.length === 0 || products.length === 0) {
    console.warn('⚠️ Reset documents skipped: clients or products data not available yet');
    return false;
  }
  
  // Generate 5 new documents
  const documents = generateFiveDocuments(clients, products);
  
  // Save to localStorage
  localStorage.setItem('facit_documents', JSON.stringify(documents));
  
  // Reset initialization flag to force reload
  localStorage.setItem('facit_initialized', 'true');
  
  console.log('Documents reset to 5 items');
  return true;
};

// Generate 5 documents with realistic data
const generateFiveDocuments = (clients, products) => {
  const documents = [];
  const categories = ['الفوترة', 'العروض التجارية', 'اللوجستيات'];
  const types = ['invoice', 'quote', 'proforma', 'delivery', 'purchase_order'];
  const titles = {
    invoice: ['فاتورة تطوير المواقع', 'فاتورة معدات تقنية', 'فاتورة تدريب', 'فاتورة خدمات سحابية'],
    quote: ['عرض سعر نظام إدارة', 'عرض سعر بنية تحتية تقنية', 'عرض سعر خدمات سحابية'],
    proforma: ['فاتورة أولية تدريب', 'فاتورة أولية معدات تقنية'],
    delivery: ['إذن تسليم معدات', 'إذن تسليم أجهزة'],
    purchase_order: ['أمر شراء خوادم', 'أمر شراء تدريب', 'أمر شراء تراخيص']
  };
  
  // Generate documents for past 6 months
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 6);
  
  // Ensure we have one of each main document type
  const mainTypes = ['invoice', 'quote', 'proforma', 'delivery', 'purchase_order'];
  
  for (let i = 1; i <= 5; i++) {
    // Use specific type for first 5 documents to ensure variety
    const type = i <= mainTypes.length ? mainTypes[i-1] : types[Math.floor(Math.random() * types.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const clientId = Math.floor(Math.random() * clients.length) + 1;
    const client = clients.find(c => c.id === clientId) || clients[0];
    const date = randomDate(startDate);
    const titleOptions = titles[type] || titles.invoice;
    const title = titleOptions[Math.floor(Math.random() * titleOptions.length)];
    const status = documentStatuses[Math.floor(Math.random() * documentStatuses.length)];
    
    // Generate items using product data
    const itemCount = Math.floor(Math.random() * 3) + 1;
    const items = [];
    let totalAmount = 0;
    
    for (let j = 0; j < itemCount; j++) {
      const product = products[Math.floor(Math.random() * products.length)];
      const quantity = Math.floor(Math.random() * 5) + 1;
      const item = {
        id: j + 1,
        name: product.name,
        quantity,
        price: product.price
      };
      items.push(item);
      totalAmount += item.price * item.quantity;
    }
    
    // Create document
    documents.push({
      id: i,
      type,
      number: `${type === 'invoice' ? 'FAC' : type === 'quote' ? 'DEV' : type === 'proforma' ? 'PRO' : type === 'delivery' ? 'BL' : 'BC'}-${1000 + i}`,
      title,
      client: client.name,
      date,
      dueDate: type === 'invoice' ? new Date(new Date(date).setDate(new Date(date).getDate() + 30)).toISOString().split('T')[0] : undefined,
      validUntil: type === 'quote' ? new Date(new Date(date).setDate(new Date(date).getDate() + 30)).toISOString().split('T')[0] : undefined,
      amount: totalAmount,
      status,
      category,
      items
    });
  }
  
  return documents;
};

/**
 * Utility to reset sample data for testing
 */

// Clears all localStorage data with 'facit_' prefix
export const clearLocalStorage = () => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith('facit_')) {
      keys.push(key);
    }
  }
  
  keys.forEach(key => localStorage.removeItem(key));
  console.log(`Cleared ${keys.length} localStorage keys`);
};

// Initialize fresh data that's good for dashboard demos
export const initializeDashboardData = () => {
  clearLocalStorage();
  
  // First, initialize basic sample data
  const data = initializeSampleData();
  
  // Then add specific test documents with good dates for charts
  const today = new Date();
  
  // Create some paid invoices for the last 30 days
  for (let i = 0; i < 10; i++) {
    const date = new Date();
    date.setDate(today.getDate() - (i * 3)); // Every 3 days
    
    const amount = 50000 + Math.floor(Math.random() * 100000);
    
    const doc = {
      id: `chart_doc_${i}`,
      number: `INV-${2023}-${3000 + i}`,
      type: 'invoice',
      client: data.clients[0].name,
      clientId: data.clients[0].id,
      date: date.toISOString().split('T')[0],
      dueDate: date.toISOString().split('T')[0],
      category: 'خدمات',
      items: [
        {
          id: data.products[0].id,
          name: data.products[0].name,
          quantity: 1,
          price: amount
        }
      ],
      status: 'paid',
      amount: amount,
      taxRate: 0.19,
      notes: "مستند لعرض الرسم البياني"
    };
    
    data.documents.push(doc);
  }
  
  // Add some paid invoices for each month of the current year
  const currentYear = today.getFullYear();
  
  for (let month = 0; month < 12; month++) {
    const date = new Date(currentYear, month, 15);
    
    // Skip future months
    if (date > today) continue;
    
    const amount = 100000 + Math.floor(Math.random() * 500000);
    
    const doc = {
      id: `chart_month_${month}`,
      number: `INV-${currentYear}-${4000 + month}`,
      type: 'invoice',
      client: data.clients[1].name,
      clientId: data.clients[1].id,
      date: date.toISOString().split('T')[0],
      dueDate: date.toISOString().split('T')[0],
      category: 'منتجات',
      items: [
        {
          id: data.products[1].id,
          name: data.products[1].name,
          quantity: 1,
          price: amount
        }
      ],
      status: 'paid',
      amount: amount,
      taxRate: 0.19,
      notes: `مستند شهري لـ ${month + 1}/${currentYear}`
    };
    
    data.documents.push(doc);
  }
  
  // Save the updated data
  localStorage.setItem('facit_documents', JSON.stringify(data.documents));
  
  // Force page reload to apply changes
  window.location.reload();
  
  return data;
};

// Resets sample data to the original state
export const resetSampleData = () => {
  clearLocalStorage();
  initializeSampleData();
  window.location.reload();
};

// Exports for usage elsewhere
export default {
  clearLocalStorage,
  resetSampleData,
  initializeDashboardData
};

export const forceReloadData = () => {
  console.log('Force reloading all sample data...');
  
  // Create test documents with guaranteed values
  const testDocuments = [
    {
      id: 9001,
      type: 'invoice',
      number: 'FAC-9001',
      title: 'فاتورة تجريبية تطوير المواقع',
      client: 'شركة التقنية المتقدمة',
      clientId: 1,
      date: new Date().toISOString().split('T')[0], // Today
      dueDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0], // 30 days from now
      amount: 250000,
      status: 'paid',
      category: 'الفوترة',
      items: [
        { id: 1, name: 'تطوير الواجهة الأمامية', quantity: 1, price: 120000 },
        { id: 2, name: 'تطوير الخادم الخلفي', quantity: 1, price: 130000 }
      ]
    },
    {
      id: 9002,
      type: 'invoice',
      number: 'FAC-9002',
      title: 'فاتورة تجريبية تدريب',
      client: 'د. فاطمة الحربي',
      clientId: 2,
      date: new Date().toISOString().split('T')[0], // Today
      dueDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0], // 30 days from now
      amount: 75000,
      status: 'pending',
      category: 'الفوترة',
      items: [
        { id: 1, name: 'تدريب React.js', quantity: 1, price: 75000 }
      ]
    },
    {
      id: 9003,
      type: 'invoice',
      number: 'FAC-9003',
      title: 'فاتورة تجريبية معدات',
      client: 'شركة ابتكار ذ.م.م',
      clientId: 3,
      date: new Date(Date.now() - 5*24*60*60*1000).toISOString().split('T')[0], // 5 days ago
      dueDate: new Date(Date.now() + 25*24*60*60*1000).toISOString().split('T')[0], // 25 days from now
      amount: 450000,
      status: 'paid',
      category: 'الفوترة',
      items: [
        { id: 1, name: 'خادم HP ProLiant', quantity: 1, price: 450000 }
      ]
    }
  ];
  
  // Save to localStorage
  const existingDocs = JSON.parse(localStorage.getItem('facit_documents') || '[]');
  const allDocs = [...existingDocs, ...testDocuments];
  localStorage.setItem('facit_documents', JSON.stringify(allDocs));
  
  console.log('Added test documents with guaranteed values:', testDocuments);
  console.log('Total documents now:', allDocs.length);
  
  // Hard reload the page
  window.location.reload();
  
  return allDocs.length;
};

